import { useTheme } from '@mui/material';
import { ChartData, ChartDataset, ChartType, DefaultDataPoint } from 'chart.js';
import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { isBlueberryFeature } from '../../../services/data/impl/cube/cubeUtils';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { selectScanDataTableExists } from '../../duckDB/duckDBSlice';
import { DuckQueryReturnOneType } from '../../duckDB/hooks/useDuckQueryScanData';
import { selectCurrFeature } from '../../select/selectSlice';
import { ScanDataEnum } from '../scanDataDefinitions';
import { useScanDataQuery } from './useScanDataQuery';
interface ChartDataWithRawData<
  TType extends ChartType = ChartType,
  TData = DefaultDataPoint<TType>,
  TLabel = unknown
> extends ChartData<TType, TData, TLabel> {
  rawData: QueryReturnType[] | null;
}

interface QueryReturnType {
  [ScanDataEnum.rowside_id]: string;
  [ScanDataEnum.rowside_num]: number;
  [ScanDataEnum.view_direction]: number;
  rowside_count: number;
}

const group = [
  ScanDataEnum.rowside_id,
  ScanDataEnum.rowside_num,
  ScanDataEnum.view_direction,
];

const order: [string, 'asc' | 'desc'][] = [[ScanDataEnum.rowside_num, 'asc']];

/**
 * Generates chart data for estimated counts in a bar chart format.
 *
 * @return {DuckQueryReturnOneType<ChartDataWithRawData<'bar'>>} The chart data for estimated counts.
 */
export const useEstimatedCountRowsideStackBar = (): DuckQueryReturnOneType<
  ChartDataWithRawData<'bar'>
> => {
  const currFeature = useAppSelector(selectCurrFeature);
  const scanDataTableExists = useAppSelector(selectScanDataTableExists);
  const { t } = useTranslation();
  const theme = useTheme();

  const cols = useMemo(() => {
    return [
      ScanDataEnum.rowside_id,
      ScanDataEnum.rowside_num,
      ScanDataEnum.view_direction,
      `FLOOR(SUM(${ScanDataEnum.estimated_count})) as rowside_count`,
    ];
  }, []);

  // Query the scan data for estimated counts based on the defined columns
  const rowsideEstimatedCounts = useScanDataQuery<QueryReturnType>({
    cols,
    group,
    order,
    // Skip the query if it's a non grape feature or if the scan data table doesn't exist
    skip: isBlueberryFeature(currFeature) || !scanDataTableExists,
  });

  // Create the chart data in chart.js bar chart format
  const chartData: ChartDataWithRawData<'bar'> = useMemo(() => {
    const datasets: ChartDataset<'bar', number[]>[] = [];
    const labels = [] as string[];

    // Generate datasets for chart.js bar chart based on the retrieved scan data
    if (rowsideEstimatedCounts.data) {
      rowsideEstimatedCounts.data.forEach((el) =>
        labels.push(
          `${el[ScanDataEnum.rowside_num]} (${el[ScanDataEnum.view_direction]})`
        )
      );

      datasets.push({
        label: t('Estimated Count', { ns: 'common' }),
        backgroundColor: theme.colors.common.purple,
        data: rowsideEstimatedCounts.data.map((el) => el.rowside_count),
      });
    }

    const chartData: ChartDataWithRawData<'bar'> = {
      datasets,
      labels,
      rawData: rowsideEstimatedCounts.data,
    };

    return chartData;
  }, [rowsideEstimatedCounts.data]);

  // Return the chart data along with the original query result
  return {
    ...rowsideEstimatedCounts,
    data: rowsideEstimatedCounts.data ? chartData : null,
  };
};
